import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, Navigate, useParams, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import Layout from "@/components/layout/Layout";
import LangRouter from "@/components/LangRouter";
import { AuthProvider } from "@/hooks/useAuth";

const Index = lazy(() => import("./pages/Index"));
const Episodes = lazy(() => import("./pages/Episodes"));
const EpisodeDetail = lazy(() => import("./pages/EpisodeDetail"));
const Players = lazy(() => import("./pages/Players"));
const PlayerDetail = lazy(() => import("./pages/PlayerDetail"));
const Tournaments = lazy(() => import("./pages/Tournaments"));
const TournamentDetail = lazy(() => import("./pages/TournamentDetail"));
const AboutSeries = lazy(() => import("./pages/AboutSeries"));
const Contact = lazy(() => import("./pages/Contact"));
const MentionsLegales = lazy(() => import("./pages/MentionsLegales"));
const PolitiqueConfidentialite = lazy(() => import("./pages/PolitiqueConfidentialite"));
const ConditionsUtilisation = lazy(() => import("./pages/ConditionsUtilisation"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const Admin = lazy(() => import("./pages/Admin"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const PageFallback = () => (
  <div className="flex min-h-[60vh] items-center justify-center">
    <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
  </div>
);

/** Redirect bare paths to /fr/... */
const LangRedirect = () => {
  const location = useLocation();
  return <Navigate to={`/fr${location.pathname}${location.search}`} replace />;
};

const publicRoutes = (
  <>
    <Route index element={<Index />} />
    <Route path="saisons" element={<Navigate to="../tournois" replace />} />
    <Route path="saisons/:id" element={<Navigate to="../tournois" replace />} />
    <Route path="episodes" element={<Episodes />} />
    <Route path="episodes/:id" element={<EpisodeDetail />} />
    <Route path="joueurs" element={<Players />} />
    <Route path="joueurs/:id" element={<PlayerDetail />} />
    <Route path="tournois" element={<Tournaments />} />
    <Route path="tournois/:id" element={<TournamentDetail />} />
    <Route path="recherche" element={<Navigate to=".." replace />} />
    <Route path="la-serie" element={<AboutSeries />} />
    <Route path="contact" element={<Contact />} />
    <Route path="mentions-legales" element={<MentionsLegales />} />
    <Route path="confidentialite" element={<PolitiqueConfidentialite />} />
    <Route path="conditions" element={<ConditionsUtilisation />} />
    <Route path="news" element={<Navigate to=".." replace />} />
  </>
);

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <Suspense fallback={<PageFallback />}>
              <Routes>
                {/* Lang-prefixed public routes */}
                <Route path="/:lang" element={<LangRouter />}>
                  <Route element={<Layout />}>
                    {publicRoutes}
                  </Route>
                </Route>

                {/* Admin routes (no lang prefix) */}
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin" element={<Admin />} />

                {/* Bare paths → redirect to /fr/... */}
                <Route path="/" element={<Navigate to="/fr" replace />} />
                <Route path="/episodes" element={<LangRedirect />} />
                <Route path="/episodes/:id" element={<LangRedirect />} />
                <Route path="/joueurs" element={<LangRedirect />} />
                <Route path="/joueurs/:id" element={<LangRedirect />} />
                <Route path="/tournois" element={<LangRedirect />} />
                <Route path="/tournois/:id" element={<LangRedirect />} />
                <Route path="/la-serie" element={<LangRedirect />} />
                <Route path="/mentions-legales" element={<LangRedirect />} />
                <Route path="/confidentialite" element={<LangRedirect />} />
                <Route path="/conditions" element={<LangRedirect />} />
                <Route path="/contact" element={<LangRedirect />} />
                <Route path="/saisons" element={<Navigate to="/fr/tournois" replace />} />
                <Route path="/saisons/:id" element={<Navigate to="/fr/tournois" replace />} />
                <Route path="/recherche" element={<Navigate to="/fr" replace />} />
                <Route path="/news" element={<Navigate to="/fr" replace />} />

                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
